import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CaretRightIcon } from "@phosphor-icons/react";

/**
 * Stripe BNPL "Pay over time" pill.
 *
 * Clickable — pressing it triggers the parent's `onCheckout` (i.e. the
 * same flow as the "Buy now" button). On the Stripe Checkout page that
 * follows, the buyer will see Card + Klarna + Affirm side-by-side as
 * payment methods (provided you've activated Klarna and Affirm at
 * dashboard.stripe.com → Settings → Payment methods).
 *
 * - Klarna: $1+ ("Pay in 4")
 * - Affirm: $50+ (monthly installments, 0% APR available)
 * - Below $1: nothing renders
 */
export default function BNPLBadge({ amount, onCheckout, disabled = false }) {
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!amount || amount <= 0) {
      setEligibility(null);
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/payments/bnpl-eligibility?amount=${amount}`);
        if (!cancelled) setEligibility(res.data);
      } catch {
        if (!cancelled) setEligibility(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amount]);

  if (!eligibility) return null;
  const { klarna_eligible, affirm_eligible, bnpl_enabled } = eligibility;
  if (!bnpl_enabled || (!klarna_eligible && !affirm_eligible)) return null;

  const klarnaPart = klarna_eligible ? `4 payments of $${(amount / 4).toFixed(2)}` : null;
  const clickable = !!onCheckout && !disabled;

  const Inner = (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex h-7 px-2 items-center justify-center rounded-full bg-[#FFA8CD] text-black font-bold text-[11px] tracking-wide">
          Klarna
        </span>
        <span className="inline-flex h-7 px-2 items-center justify-center rounded-full bg-[#0FA0EA] text-white font-bold text-[11px] tracking-wide">
          Affirm
        </span>
        <span className="font-bold text-zinc-800 flex-1">Pay over time</span>
        {clickable && (
          <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-violet-700 uppercase tracking-wider">
            Use at checkout <CaretRightIcon size={11} weight="bold" />
          </span>
        )}
      </div>
      <p className="text-zinc-600 mt-1.5 leading-snug text-sm">
        {klarna_eligible && affirm_eligible
          ? `Split into ${klarnaPart} with Klarna or pick monthly installments with Affirm.`
          : klarna_eligible
          ? `Split into ${klarnaPart} with Klarna at checkout.`
          : `Choose monthly installments with Affirm at checkout.`}
      </p>
    </>
  );

  const baseClass =
    "mt-3 w-full text-left rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 to-pink-50 p-3 transition";

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onCheckout}
        className={`${baseClass} hover:from-violet-100 hover:to-pink-100 hover:border-violet-300 active:scale-[0.99] cursor-pointer`}
        data-testid="bnpl-badge"
      >
        {Inner}
      </button>
    );
  }
  return (
    <div className={baseClass} data-testid="bnpl-badge">
      {Inner}
    </div>
  );
}
